import { useMemo, useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useDashboardData, getWeekRange, getMonthRange, getYearRange, formatDateShort, padMonthlyRevenue } from "@/hooks/useDashboardData";
import { usePreferences } from "@/hooks/usePreferences";
import { getDashboardRevenue } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { CalendarDays, ChevronRight, ChevronLeft, Database, Package, SlidersHorizontal } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { CrewAvailabilityWidget } from "@/components/CrewAvailabilityWidget";
import { DashboardAlerts } from "@/components/dashboard/DashboardAlerts";
import { StatCards } from "@/components/dashboard/StatCards";
import { RevenueCharts } from "@/components/dashboard/RevenueCharts";

// Panels a user can show/hide. Visibility choices live in per-user
// preferences (dashboard_panels); defaultOff panels start hidden.
const PANEL_DEFS = [
  { key: "alerts", label: "Alerts (conflicts & shortages)", roles: ["owner", "manager"] },
  { key: "stats", label: "Stat cards", roles: ["owner", "manager"] },
  { key: "monthly_revenue", label: "Monthly revenue", roles: ["owner"] },
  { key: "client_revenue", label: "Revenue by client", roles: ["owner"] },
  { key: "gear_revenue", label: "Revenue by gear", roles: ["owner"], defaultOff: true },
  { key: "gear_status", label: "Gear availability", roles: ["owner", "manager"] },
  { key: "upcoming", label: "Upcoming events", roles: ["owner", "manager"] },
  { key: "crew_availability", label: "Crew availability", roles: ["owner", "manager"] },
];

const CHART_TOOLTIP_STYLE = { background: "#18181b", border: "1px solid #27272a", borderRadius: 4 };
const CHART_LABEL_STYLE = { color: "#fafafa", fontWeight: 600 };
const CHART_ITEM_STYLE = { color: "#fafafa" };

function getWeekBounds() {
  const now = new Date();
  const day = now.getDay();
  const sun = new Date(now); sun.setDate(now.getDate() - day); sun.setHours(0, 0, 0, 0);
  const satThisWeek = new Date(sun); satThisWeek.setDate(sun.getDate() + 6);
  return { thisWeekEnd: satThisWeek.toISOString().split('T')[0] };
}

function UpcomingEventsCard({ events, navigate, colSpan = "lg:col-span-7" }) {
  const { thisWeekEnd } = useMemo(() => getWeekBounds(), []);
  const thisWeekEvents = events.filter(ev => ev.start_date <= thisWeekEnd);
  const nextWeekEvents = events.filter(ev => ev.start_date > thisWeekEnd);

  const renderEvent = (ev) => (
    <div key={ev.event_id} data-testid={`upcoming-event-${ev.event_id}`}
      className="flex items-center justify-between py-2.5 px-3 rounded-sm bg-secondary/30 border border-border cursor-pointer hover:bg-secondary/60 hover:border-primary/30 transition-colors duration-150"
      onClick={() => navigate('/events', { state: { highlightEvent: ev.event_id, eventTab: ev.status === 'pending' ? 'pending' : ev.status === 'declined' ? 'declined' : ev.status === 'completed' ? 'archive' : 'upcoming' } })}
      role="button" tabIndex={0}>
      <div className="min-w-0"><p className="text-sm font-medium truncate">{ev.name}</p><p className="text-xs text-muted-foreground">{ev.venue} &middot; {ev.start_date}</p></div>
      <div className="flex items-center gap-2 shrink-0"><Badge variant="outline" className={`text-xs status-${ev.status}`}>{ev.status}</Badge><ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /></div>
    </div>
  );

  return (
    <Card className={`${colSpan} border-border`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <CalendarDays className="w-4 h-4" /> Upcoming Events <span className="text-xs font-normal normal-case text-muted-foreground/60 ml-1">Next 2 weeks</span>
        </CardTitle>
      </CardHeader>
      <CardContent data-testid="upcoming-events-list">
        {events.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No upcoming events in the next 2 weeks</p> : (
          <div className="space-y-4">
            {thisWeekEvents.length > 0 && <div className="space-y-2"><p data-testid="this-week-label" className="text-xs font-semibold uppercase tracking-wider text-primary/80">This Week</p><div className="space-y-2">{thisWeekEvents.map(renderEvent)}</div></div>}
            {nextWeekEvents.length > 0 && <div className="space-y-2"><p data-testid="next-week-label" className="text-xs font-semibold uppercase tracking-wider text-blue-400/80 pt-1 border-t border-border">Next Week</p><div className="space-y-2">{nextWeekEvents.map(renderEvent)}</div></div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user, role } = useOutletContext();
  const navigate = useNavigate();
  const { stats, conflicts, shortages, loading, seeding, dateStart, dateEnd, setDateStart, setDateEnd, handleSeed } = useDashboardData(role);
  const isOwner = role === "owner";

  const { prefs, setPref } = usePreferences();
  const panelPrefs = prefs.dashboard_panels || {};
  const myPanels = PANEL_DEFS.filter(p => p.roles.includes(role));
  const isVisible = (key) => {
    const def = PANEL_DEFS.find(p => p.key === key);
    return panelPrefs[key] ?? !def?.defaultOff;
  };
  const togglePanel = (key, checked) => {
    setPref("dashboard_panels", { ...panelPrefs, [key]: checked });
  };

  // Revenue-by-gear data comes from the heavier revenue endpoint — only
  // fetched when that panel is actually enabled.
  const [gearRevenue, setGearRevenue] = useState([]);
  const showGearRevenue = isOwner && isVisible("gear_revenue");
  useEffect(() => {
    if (!showGearRevenue) return;
    getDashboardRevenue(dateStart, dateEnd)
      .then(res => setGearRevenue(res.data.gear_revenue || []))
      .catch(() => setGearRevenue([]));
  }, [showGearRevenue, dateStart, dateEnd]);

  const monthlyData = useMemo(() => {
    if (!stats?.monthly_revenue) return [];
    return padMonthlyRevenue(stats.monthly_revenue, dateStart, dateEnd);
  }, [stats?.monthly_revenue, dateStart, dateEnd]);

  const clientData = useMemo(() => {
    return stats?.client_revenue ? Object.entries(stats.client_revenue).map(([name, rev]) => ({ name, revenue: rev })) : [];
  }, [stats?.client_revenue]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  // Crew see a personal schedule, not company-wide stats.
  if (role === "crew") {
    return (
      <div data-testid="dashboard-page" className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>My Schedule</h1>
          <p className="text-muted-foreground text-sm mt-1">Welcome back, {user?.name?.split(' ')[0]} — here are your upcoming shows</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <UpcomingEventsCard events={stats?.upcoming_events || []} navigate={navigate} />
        </div>
      </div>
    );
  }

  const gearStatusData = [
    { name: "Available", value: stats?.gear_available || 0, color: "#10b981" },
    { name: "In Use", value: stats?.gear_in_use || 0, color: "#3b82f6" },
    { name: "Damaged", value: stats?.gear_damaged || 0, color: "#ef4444" },
    { name: "Maintenance", value: stats?.gear_maintenance || 0, color: "#f59e0b" },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Welcome back, {user?.name?.split(' ')[0]}</p>
        </div>
        <div className="flex items-center gap-2">
          {isOwner && (
            <Button data-testid="seed-data-btn" onClick={handleSeed} disabled={seeding} variant="secondary" className="text-xs uppercase tracking-wider">
              <Database className="w-3.5 h-3.5 mr-2" />{seeding ? "Seeding..." : "Load Demo Data"}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="customize-dashboard-btn" variant="secondary" className="text-xs uppercase tracking-wider">
                <SlidersHorizontal className="w-3.5 h-3.5 mr-2" /> Customize
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">Dashboard Panels</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {myPanels.map(p => (
                <DropdownMenuCheckboxItem
                  key={p.key}
                  data-testid={`panel-toggle-${p.key}`}
                  checked={isVisible(p.key)}
                  onCheckedChange={(checked) => togglePanel(p.key, checked)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {p.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Date Range Picker */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <CalendarDays className="w-4 h-4 text-primary shrink-0" />
            <Button data-testid="dash-prev-month" variant="ghost" size="icon" className="h-8 w-8"
              onClick={() => { const d = new Date(dateStart + 'T00:00:00'); d.setMonth(d.getMonth() - 1); const r = getMonthRange(d); setDateStart(r.start); setDateEnd(r.end); }}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-semibold min-w-[180px] text-center">{formatDateShort(dateStart)} — {formatDateShort(dateEnd)}</span>
            <Button data-testid="dash-next-month" variant="ghost" size="icon" className="h-8 w-8"
              onClick={() => { const d = new Date(dateStart + 'T00:00:00'); d.setMonth(d.getMonth() + 1); const r = getMonthRange(d); setDateStart(r.start); setDateEnd(r.end); }}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button data-testid="dash-this-week" variant="secondary" size="sm" className="text-xs uppercase tracking-wider h-8"
              onClick={() => { const r = getWeekRange(new Date()); setDateStart(r.start); setDateEnd(r.end); }}>This Week</Button>
            <Button data-testid="dash-this-month" variant="secondary" size="sm" className="text-xs uppercase tracking-wider h-8"
              onClick={() => { const r = getMonthRange(new Date()); setDateStart(r.start); setDateEnd(r.end); }}>This Month</Button>
            <Button data-testid="dash-this-year" variant="secondary" size="sm" className="text-xs uppercase tracking-wider h-8"
              onClick={() => { const r = getYearRange(new Date()); setDateStart(r.start); setDateEnd(r.end); }}>This Year</Button>
            <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
              <Input data-testid="dash-start-date" type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} className="flex-1 min-w-0 sm:flex-none sm:w-36 h-8 text-xs bg-secondary/30 border-border" />
              <span className="text-xs text-muted-foreground">to</span>
              <Input data-testid="dash-end-date" type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} className="flex-1 min-w-0 sm:flex-none sm:w-36 h-8 text-xs bg-secondary/30 border-border" />
            </div>
          </div>
        </CardContent>
      </Card>

      {isVisible("alerts") && <DashboardAlerts conflicts={conflicts} shortages={shortages} />}
      {isVisible("stats") && <StatCards stats={stats} isOwner={isOwner} />}
      {isOwner && (
        <RevenueCharts
          monthlyData={monthlyData}
          clientData={clientData}
          gearData={gearRevenue}
          show={{ monthly: isVisible("monthly_revenue"), client: isVisible("client_revenue"), gear: showGearRevenue }}
        />
      )}

      {/* Gear Status + Upcoming Events */}
      {(isVisible("gear_status") || isVisible("upcoming")) && (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {isVisible("gear_status") && (
        <Card className="lg:col-span-5 border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Package className="w-4 h-4" /> Gear Availability
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="gear-availability-chart">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={gearStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={75} strokeWidth={1} stroke="#09090b">
                  {gearStatusData.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} itemStyle={CHART_ITEM_STYLE} labelStyle={CHART_LABEL_STYLE} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-4 mt-2 justify-center">
              {gearStatusData.map((d) => (
                <div key={d.name} className="flex items-center gap-1.5 text-xs"><div className="w-2 h-2 rounded-full" style={{ background: d.color }} /><span className="text-muted-foreground">{d.name}: {d.value}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
        )}
        {isVisible("upcoming") && (
          <UpcomingEventsCard events={stats?.upcoming_events || []} navigate={navigate} colSpan={isVisible("gear_status") ? "lg:col-span-7" : "lg:col-span-12"} />
        )}
      </div>
      )}

      {isVisible("crew_availability") && <CrewAvailabilityWidget />}
    </div>
  );
}
